import React, { useState, useEffect } from 'react';
import { Box, TextField, IconButton, Popover, Typography } from '@mui/material';
import { Close as CloseIcon, Colorize as ColorizeIcon, SwapVert as SwapVertIcon } from '@mui/icons-material';

// @ts-ignore
import * as ReactColorful from 'react-colorful';
const { HexColorPicker } = ReactColorful;

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
}

const isValidColorCode = (color: string): boolean => {
  if (!color) return false;
  // HEX形式（#RRGGBB）またはrgba()形式を許可
  return /^#[0-9A-Fa-f]{6}$/.test(color) || /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(color);
};

const parseRgba = (color: string): { r: number; g: number; b: number; a: number } | null => {
  if (!color) return null;
  
  // rgba()形式をパース
  const rgbaMatch = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(,\s*([\d.]+)\s*)?\)$/i);
  if (rgbaMatch) {
    return {
      r: parseInt(rgbaMatch[1], 10),
      g: parseInt(rgbaMatch[2], 10),
      b: parseInt(rgbaMatch[3], 10),
      a: rgbaMatch[5] ? parseFloat(rgbaMatch[5]) : 1,
    };
  }
  
  // HEX形式をパース
  const hexMatch = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (hexMatch) {
    return {
      r: parseInt(hexMatch[1], 16),
      g: parseInt(hexMatch[2], 16),
      b: parseInt(hexMatch[3], 16),
      a: 1,
    };
  }
  
  return null;
};

const rgbaToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => {
    const hex = Math.max(0, Math.min(255, Math.round(n))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

const rgbaToString = (r: number, g: number, b: number, a: number): string => {
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a.toFixed(2)})`;
};

const isDarkColor = (color: string): boolean => {
  if (!color || !isValidColorCode(color)) return false;
  const rgba = parseRgba(color);
  if (!rgba) return false;
  const luminance = 0.299 * rgba.r + 0.587 * rgba.g + 0.114 * rgba.b;
  return luminance < 128;
};

const formatColorCode = (color: string): string => {
  if (!color || !isValidColorCode(color)) return '# F F F F F F';
  const rgba = parseRgba(color);
  if (rgba && color.startsWith('rgba')) {
    // rgba形式の場合はHEXに変換して表示
    return rgbaToHex(rgba.r, rgba.g, rgba.b).toUpperCase().split('').join(' ');
  }
  return color.toUpperCase().split('').join(' ');
};

const colorPickerStyles = {
  colorBox: {
    position: 'relative' as const,
    backgroundColor: '#FFFFFF',
    border: '1px solid #D0D0D0',
    borderRadius: '12px',
    p: 1.5,
    height: 120,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    cursor: 'pointer',
  },
  colorDisplay: {
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    borderRadius: '8px',
    padding: 0,
    margin: 0,
  },
  colorText: {
    position: 'absolute' as const,
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none' as const,
  },
  deleteButton: {
    position: 'absolute' as const,
    top: 12,
    right: 12,
    width: 24,
    height: 24,
    padding: 0,
    backgroundColor: 'transparent',
    pointerEvents: 'auto' as const,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  popover: {
    p: 0,
    borderRadius: 0,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#FFFFFF',
    width: 220,
    overflow: 'hidden' as const,
  },
  saturationArea: {
    position: 'relative' as const,
    '& .react-colorful': {
      width: '100%',
      height: 120,
    },
    '& .react-colorful__saturation': {
      borderRadius: 0,
      border: 'none',
      height: 120,
    },
    '& .react-colorful__hue': {
      display: 'none',
    },
    '& .react-colorful__pointer': {
      width: 10,
      height: 10,
      backgroundColor: '#ffffff !important',
      border: '1px solid #000000 !important',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      '& > div': {
        backgroundColor: '#ffffff !important',
        border: 'none !important',
      },
    },
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    p: 1,
    borderTop: '1px solid #e0e0e0',
  },
  eyedropperButton: {
    width: 24,
    height: 24,
    padding: 0,
    minWidth: 24,
  },
  colorSwatch: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '1px solid #dadce0',
    flexShrink: 0,
    mr: 1,
  },
  hueSlider: {
    flex: 1,
    height: 10,
    position: 'relative' as const,
    '& .react-colorful': {
      width: '100%',
      height: '100%',
    },
    '& .react-colorful__saturation': {
      display: 'none',
    },
    '& .react-colorful__hue': {
      borderRadius: '2px',
      border: 'none',
      height: '100%',
    },
    '& .react-colorful__pointer': {
      width: 14,
      height: 14,
      border: '1px solid #ffffff',
      boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
    },
  },
  hexInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    p: 1.5,
    borderTop: '1px solid #e0e0e0',
  },
  hexLabel: {
    fontSize: '13px',
    color: '#5f6368',
    minWidth: 30,
    fontWeight: 400,
  },
  hexInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: 'monospace',
      height: 28,
      backgroundColor: '#f8f9fa',
      '& fieldset': {
        borderColor: '#dadce0',
      },
      '&:hover fieldset': {
        borderColor: '#bdc1c6',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1a73e8',
        borderWidth: '2px',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '4px 10px',
    },
  },
  rgbaInputRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    p: 1.5,
    borderTop: '1px solid #e0e0e0',
  },
  rgbaInputsContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0.25,
    width: '100%',
  },
  rgbaLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.25,
    width: '100%',
  },
  rgbaInputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0.25,
  },
  rgbaInputLabel: {
    fontSize: '11px',
    color: '#5f6368',
    fontWeight: 400,
  },
  rgbaInput: {
    width: 40,
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: 'monospace',
      height: 28,
      backgroundColor: '#f8f9fa',
      '& fieldset': {
        borderColor: '#dadce0',
      },
      '&:hover fieldset': {
        borderColor: '#bdc1c6',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1a73e8',
        borderWidth: '2px',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '4px 4px',
      textAlign: 'center',
    },
  },
  toggleButton: {
    width: 24,
    height: 24,
    padding: 0,
    minWidth: 24,
    ml: 0.5,
  },
};

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hexInput, setHexInput] = useState<string>(value || '#ffffff');
  const [hexError, setHexError] = useState<string | null>(null);
  const [inputMode, setInputMode] = useState<'hex' | 'rgba'>('hex');
  const [rgbaInput, setRgbaInput] = useState<{ r: string; g: string; b: string; a: string }>({ r: '255', g: '255', b: '255', a: '1' });

  const getValidColor = (): string => {
    if (!value) return '#ffffff';
    const rgba = parseRgba(value);
    if (rgba) {
      return rgbaToHex(rgba.r, rgba.g, rgba.b);
    }
    return isValidColorCode(value) ? value : '#ffffff';
  };

  const validColor = getValidColor();

  useEffect(() => {
    if (value) {
      const rgba = parseRgba(value);
      if (rgba) {
        // rgba形式の場合
        setRgbaInput({
          r: rgba.r.toString(),
          g: rgba.g.toString(),
          b: rgba.b.toString(),
          a: rgba.a.toString(),
        });
        setHexInput(rgbaToHex(rgba.r, rgba.g, rgba.b));
      } else if (isValidColorCode(value)) {
        // HEX形式の場合
        setHexInput(value);
        const parsed = parseRgba(value);
        if (parsed) {
          setRgbaInput({
            r: parsed.r.toString(),
            g: parsed.g.toString(),
            b: parsed.b.toString(),
            a: '1',
          });
        }
      }
      setHexError(null);
    }
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorChange = (newColor: string) => {
    if (isValidColorCode(newColor)) {
      const rgba = parseRgba(newColor);
      if (rgba) {
        // HEXカラーピッカーから変更された場合、現在の透明度を保持
        const currentRgba = parseRgba(value || '#ffffff');
        const alpha = currentRgba ? currentRgba.a : 1;
        const newValue = alpha < 1 ? rgbaToString(rgba.r, rgba.g, rgba.b, alpha) : rgbaToHex(rgba.r, rgba.g, rgba.b);
        onChange(newValue);
        setHexInput(rgbaToHex(rgba.r, rgba.g, rgba.b));
        setRgbaInput({
          r: rgba.r.toString(),
          g: rgba.g.toString(),
          b: rgba.b.toString(),
          a: Math.round(alpha * 100).toString(),
        });
      } else {
        onChange(newColor);
        setHexInput(newColor);
      }
      setHexError(null);
    }
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setHexInput(input);

    let formattedInput = input;
    if (!formattedInput.startsWith('#')) {
      formattedInput = '#' + formattedInput;
    }

    if (/^#[0-9A-Fa-f]{0,6}$/.test(formattedInput)) {
      if (formattedInput.length === 7 && isValidColorCode(formattedInput)) {
        onChange(formattedInput);
        setHexError(null);
      } else {
        setHexError(null);
      }
    }
  };

  const handleHexInputBlur = () => {
    if (isValidColorCode(hexInput)) {
      const rgba = parseRgba(value || '#ffffff');
      const alpha = rgba ? rgba.a : 1;
      const newValue = alpha < 1 ? rgbaToString(parseRgba(hexInput)!.r, parseRgba(hexInput)!.g, parseRgba(hexInput)!.b, alpha) : hexInput;
      onChange(newValue);
      setHexError(null);
      const parsed = parseRgba(hexInput);
        if (parsed) {
          setRgbaInput({
            r: parsed.r.toString(),
            g: parsed.g.toString(),
            b: parsed.b.toString(),
            a: alpha.toString(),
          });
        }
    } else {
      setHexInput(validColor);
      setHexError(null);
    }
  };

  const handleRgbaInputChange = (channel: 'r' | 'g' | 'b' | 'a', input: string) => {
    if (channel === 'a') {
      // アルファは0-1の範囲
      const numValue = input.replace(/[^0-9.]/g, '');
      const floatValue = parseFloat(numValue);
      if (numValue === '' || (!isNaN(floatValue) && floatValue >= 0 && floatValue <= 1)) {
        setRgbaInput((prev) => ({
          ...prev,
          [channel]: numValue,
        }));

        if (numValue !== '' && !isNaN(floatValue)) {
          const alpha = Math.max(0, Math.min(1, floatValue));
          const r = parseInt(rgbaInput.r || '0');
          const g = parseInt(rgbaInput.g || '0');
          const b = parseInt(rgbaInput.b || '0');
          const newValue = alpha < 1 ? rgbaToString(r, g, b, alpha) : rgbaToHex(r, g, b);
          onChange(newValue);
        }
      }
    } else {
      // R, G, Bは0-255
      const numValue = input.replace(/[^0-9]/g, '');
      if (numValue === '' || (parseInt(numValue) >= 0 && parseInt(numValue) <= 255)) {
        setRgbaInput((prev) => ({
          ...prev,
          [channel]: numValue,
        }));

        if (numValue !== '' && parseInt(numValue) >= 0 && parseInt(numValue) <= 255) {
          const r = channel === 'r' ? parseInt(numValue) : parseInt(rgbaInput.r || '0');
          const g = channel === 'g' ? parseInt(numValue) : parseInt(rgbaInput.g || '0');
          const b = channel === 'b' ? parseInt(numValue) : parseInt(rgbaInput.b || '0');
          const alpha = parseFloat(rgbaInput.a || '1');
          const newValue = alpha < 1 ? rgbaToString(r, g, b, alpha) : rgbaToHex(r, g, b);
          onChange(newValue);
          setHexInput(rgbaToHex(r, g, b));
        }
      }
    }
  };

  const handleRgbaInputBlur = (channel: 'r' | 'g' | 'b' | 'a') => {
    const numValue = rgbaInput[channel];
    if (numValue === '') {
      const rgba = parseRgba(value || '#ffffff');
      if (rgba) {
        if (channel === 'a') {
          setRgbaInput((prev) => ({
            ...prev,
            [channel]: rgba.a.toString(),
          }));
        } else {
          setRgbaInput((prev) => ({
            ...prev,
            [channel]: rgba[channel].toString(),
          }));
        }
      }
    } else {
      if (channel === 'a') {
        const num = parseFloat(numValue);
        if (isNaN(num) || num < 0) {
          setRgbaInput((prev) => ({
            ...prev,
            [channel]: '0',
          }));
        } else if (num > 1) {
          setRgbaInput((prev) => ({
            ...prev,
            [channel]: '1',
          }));
        }
      } else {
        const num = parseInt(numValue);
        if (isNaN(num) || num < 0) {
          setRgbaInput((prev) => ({
            ...prev,
            [channel]: '0',
          }));
        } else if (num > 255) {
          setRgbaInput((prev) => ({
            ...prev,
            [channel]: '255',
          }));
        }
      }
    }
  };

  const handleToggleInputMode = () => {
    setInputMode((prev) => (prev === 'hex' ? 'rgba' : 'hex'));
  };

  const open = Boolean(anchorEl);
  const textColor = isDarkColor(validColor) ? '#FFFFFF' : '#000000';

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          ...colorPickerStyles.colorBox,
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        <Box
          component="div"
          sx={{
            ...colorPickerStyles.colorDisplay,
            backgroundColor: validColor,
          }}
        />
        <Box sx={colorPickerStyles.colorText}>
          <Typography
            sx={{
              fontSize: 13,
              color: textColor,
              fontFamily: 'monospace',
              fontWeight: 500,
            }}
          >
            {formatColorCode(validColor)}
          </Typography>
        </Box>
        {validColor !== '#FFFFFF' && validColor !== '#ffffff' && !disabled && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onChange('#FFFFFF');
            }}
            sx={colorPickerStyles.deleteButton}
          >
            <CloseIcon sx={{ fontSize: 18, color: textColor }} />
          </IconButton>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: colorPickerStyles.popover,
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={colorPickerStyles.saturationArea}>
            <HexColorPicker color={validColor} onChange={handleColorChange} />
          </Box>

          <Box sx={colorPickerStyles.controlsRow}>
            <IconButton size="small" sx={colorPickerStyles.eyedropperButton}>
              <ColorizeIcon sx={{ fontSize: 16, color: '#5f6368' }} />
            </IconButton>

            <Box
              sx={{
                ...colorPickerStyles.colorSwatch,
                backgroundColor: validColor,
              }}
            />

            <Box sx={colorPickerStyles.hueSlider}>
              <HexColorPicker color={validColor} onChange={handleColorChange} />
            </Box>
          </Box>

          {inputMode === 'hex' ? (
            <Box sx={colorPickerStyles.hexInputRow}>
              <Typography variant="body2" sx={colorPickerStyles.hexLabel}>
                HEX
              </Typography>
              <TextField
                value={hexInput}
                onChange={handleHexInputChange}
                onBlur={handleHexInputBlur}
                placeholder="#FFFFFF"
                size="small"
                error={!!hexError}
                helperText={hexError}
                sx={colorPickerStyles.hexInput}
              />
              <IconButton
                size="small"
                onClick={handleToggleInputMode}
                sx={colorPickerStyles.toggleButton}
              >
                <SwapVertIcon sx={{ fontSize: 16, color: '#5f6368' }} />
              </IconButton>
            </Box>
          ) : (
            <Box sx={colorPickerStyles.rgbaInputRow}>
              <Box sx={colorPickerStyles.rgbaInputsContainer}>
                <Box sx={colorPickerStyles.rgbaInputWrapper}>
                  <TextField
                    value={rgbaInput.r}
                    onChange={(e) => handleRgbaInputChange('r', e.target.value)}
                    onBlur={() => handleRgbaInputBlur('r')}
                    placeholder="255"
                    size="small"
                    sx={colorPickerStyles.rgbaInput}
                    inputProps={{ maxLength: 3 }}
                  />
                </Box>
                <Box sx={colorPickerStyles.rgbaInputWrapper}>
                  <TextField
                    value={rgbaInput.g}
                    onChange={(e) => handleRgbaInputChange('g', e.target.value)}
                    onBlur={() => handleRgbaInputBlur('g')}
                    placeholder="255"
                    size="small"
                    sx={colorPickerStyles.rgbaInput}
                    inputProps={{ maxLength: 3 }}
                  />
                </Box>
                <Box sx={colorPickerStyles.rgbaInputWrapper}>
                  <TextField
                    value={rgbaInput.b}
                    onChange={(e) => handleRgbaInputChange('b', e.target.value)}
                    onBlur={() => handleRgbaInputBlur('b')}
                    placeholder="255"
                    size="small"
                    sx={colorPickerStyles.rgbaInput}
                    inputProps={{ maxLength: 3 }}
                  />
                </Box>
                <Box sx={colorPickerStyles.rgbaInputWrapper}>
                  <TextField
                    value={rgbaInput.a}
                    onChange={(e) => handleRgbaInputChange('a', e.target.value)}
                    onBlur={() => handleRgbaInputBlur('a')}
                    placeholder="1"
                    size="small"
                    sx={colorPickerStyles.rgbaInput}
                    inputProps={{ maxLength: 3 }}
                  />
                </Box>
              </Box>
              <Box sx={colorPickerStyles.rgbaLabelRow}>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="caption" sx={colorPickerStyles.rgbaInputLabel}>
                    R
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="caption" sx={colorPickerStyles.rgbaInputLabel}>
                    G
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="caption" sx={colorPickerStyles.rgbaInputLabel}>
                    B
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="caption" sx={colorPickerStyles.rgbaInputLabel}>
                    A
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleToggleInputMode}
                  sx={colorPickerStyles.toggleButton}
                >
                  <SwapVertIcon sx={{ fontSize: 16, color: '#5f6368' }} />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};
